/**
 * Mark as duplicate (case level, 2026-10-29).
 *
 * Isaiah Cotto: "PAs are saying they can't mark duplicates ... I need it." DUPLICATE was written only by the owner's
 * closer and by Ready to submit closing a resident's copies; a PA who could see two cases for one resident had no way
 * to say so.
 *
 * The act closes THIS case as a duplicate of ANOTHER case of the same resident. It needs a choice (which case it
 * duplicates) and a note, so it is its own act rather than a word in the status dropdown. Three steps, inline in the
 * case panel like its neighbours (the docked panel is itself the modal surface, so this is not a second dialog):
 *   1. the resident's other cases, each with its kind, status, holder and dates (never a name or an SSN); a case that
 *      cannot be chosen says why, in the server's words
 *   2. the note, required (kept on the record)
 *   3. a confirmation that says what will happen; focus moves to it, Escape steps back
 * The server decides everything again (advocateNativeService.markDuplicate); this only offers what it would accept.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  advocateApi, freshKey, shortDate, STAGE_LABEL, STATUS_WORD_LABEL,
} from './advocateApi';
import type { DuplicateCandidate, DuplicateCandidates, Stage, StatusWord } from './advocateApi';

type Done = (msg: string, opts?: { stop?: boolean }) => Promise<void>;

export interface MarkDuplicateFormProps {
  caseId: string;
  onDone: Done;
  /** How a case is linked from a refusal ("work that one"): the shell passes its router link. Plain text otherwise. */
  caseLink?: (caseId: string, label: string) => React.ReactNode;
}

const NOTE_MIN = 3;
const NOTE_MAX = 1800;
export const shortCase = (id: string): string => `case ${String(id).slice(0, 8)}`;

/** The case's status in the PA's words: its word when it has one, else its stage. */
function wordOf(c: { status_word: string | null; stage: string }): string {
  if (c.status_word) return STATUS_WORD_LABEL[c.status_word as StatusWord] || (c.status_word === 'DUPLICATE' ? 'Duplicate' : c.status_word);
  return STAGE_LABEL[c.stage as Stage] || c.stage.toLowerCase().replace(/_/g, ' ');
}

/** One line per candidate: "Lead · Vetting with the facility · held by Pat · opened Oct 1, 2026 · case 1a2b3c4d". */
export function describeCandidate(c: DuplicateCandidate): string {
  return [
    c.kind === 'REPLACEMENT' ? 'Replacement' : 'Lead',
    wordOf(c),
    c.holder_name ? `held by ${c.holder_name}` : 'unassigned',
    c.opened_on ? `opened ${shortDate(c.opened_on)}` : null,
    c.closed_on ? `closed ${shortDate(c.closed_on)}` : null,
    shortCase(c.id),
  ].filter(Boolean).join(' · ');
}

/** The words for each refusal, used when the server's own sentence is missing. The server's sentence wins. */
const REFUSAL_WORDS: Record<string, string> = {
  REASON_REQUIRED: 'Say why this case is a duplicate. The note stays on the record.',
  KEEP_REQUIRED: 'Choose the case this one duplicates.',
  SAME_CASE: 'A case cannot be a duplicate of itself. Choose the other case.',
  KEEP_NOT_FOUND: 'The case you chose was not found. Reload and choose again.',
  NOT_THE_SAME_RESIDENT: 'The case you chose belongs to a different resident. A case is only a duplicate of another case of the same resident.',
  DIFFERENT_EPISODE_KIND: 'A replacement and an ordinary lead are separate pieces of work, so one is not a duplicate of the other.',
  DUPLICATE_CYCLE: 'The case you chose was itself closed as a duplicate of this one. This is the case to work.',
  KEEP_IS_DUPLICATE: 'The case you chose was itself closed as a duplicate of another case. Choose the case it duplicates instead.',
  RECORDED_LEAD: 'This case carries the lead recorded in Manifest Review, so it is the case to keep. Mark the other case as the duplicate of this one.',
  NO_RESIDENT: 'This case is not linked to a resident, so it cannot be a duplicate of another case.',
  ALREADY_DUPLICATE: 'This case is already closed as a duplicate of another case for this resident.',
  ALREADY_CLOSED: 'This case is already closed. If it should be worked, use "Reopen this lead".',
  HANDED_TO_UMED: 'This case has gone to UMED, so it is the case to keep. Mark the other case as the duplicate of this one.',
  HAS_SALE: 'This case carries a sale, so it is the case to keep. Mark the other case as the duplicate of this one.',
  WITH_FINANCE: 'This case was handed to Finance as private pay. Finance must return it before it can be closed.',
  HELD_BY_VA: "A VA is verifying this resident's benefits (Needs verification). Mark a duplicate after the VA answers.",
  KEEP_IS_CLOSED: "The case you chose is closed, so nothing of this resident's would remain open. Choose an open case, or reopen that one first if the resident came back.",
  NOT_FOUND: 'This case was not found. Reload it and try again.',
};

/** The client's own placeholder when the server sent no sentence (services/api.ts): not words to show anyone. */
const CLIENT_PLACEHOLDER = 'Request failed';

/**
 * A failed call, in words a person can act on. Every refusal says that nothing changed.
 * A known code is said in this list's words FIRST (verifier on #542: the client always sets `message`, to its own
 * placeholder when the server sent none, so a fallback after the message was never reached); then the server's own
 * sentence; never the placeholder.
 */
export function refusalText(r: { code?: string; message?: string; error?: string; status?: number }): string {
  if (r.status === undefined) return 'Could not reach the server. Nothing was changed.';
  if (r.status === 429) return 'Too many requests from your account in the last few minutes. Nothing was changed. Wait a few minutes, then try again.';
  if (r.status >= 500) return 'The server failed while recording this. Nothing was changed, and it is not a permissions problem.';
  if (r.code === 'DENY_RESOURCE_RELATIONSHIP' || r.code === 'DENY_NO_CAPABILITY' || r.status === 403) return 'This case is not yours to change, so it was not marked. Nothing was changed.';
  const server = [r.message, r.error].find((s) => s && s !== CLIENT_PLACEHOLDER);
  const said = (r.code && REFUSAL_WORDS[r.code]) || server || 'That did not go through.';
  return /Nothing was changed/.test(said) ? said : `${said} Nothing was changed.`;
}

export default function MarkDuplicateForm({ caseId, onDone, caseLink }: MarkDuplicateFormProps) {
  const [data, setData] = useState<DuplicateCandidates | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState('');
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<React.ReactNode>(null);
  // One retry key per choice and note: a retry after a dropped connection replays; a changed choice is a new act.
  const key = useRef<string>(freshKey());
  const confirmRef = useRef<HTMLButtonElement | null>(null);
  // Back or Escape returns focus to the button that opened the confirmation, never to the page (verifier on #542).
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const returnFocus = useRef(false);
  const link = (id: string, label: string) => (caseLink ? caseLink(id, label) : label);

  const load = useCallback(async () => {
    setLoading(true); setLoadErr(null);
    try {
      const r = await advocateApi.duplicateCandidates(caseId);
      if (!r.success || !r.data) { setLoadErr(refusalText(r).replace(/ Nothing was changed\.?$/, '')); return; }
      setData(r.data);
    } catch {
      setLoadErr('Could not reach the server.');
    } finally { setLoading(false); }
  }, [caseId]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (confirming) confirmRef.current?.focus();
    else if (returnFocus.current) { returnFocus.current = false; triggerRef.current?.focus(); }
  }, [confirming]);

  const fresh = () => { key.current = freshKey(); setConfirming(false); };
  const stepBack = () => { returnFocus.current = true; setConfirming(false); };
  const refuse = (m: string, shown?: React.ReactNode) => { setErr(shown ?? m); stepBack(); void onDone(m, { stop: true }); };

  if (loading) return <p className="pa-text none" role="status">Looking up this resident&apos;s other cases…</p>;
  if (loadErr) {
    return (
      <div role="alert" className="pa-notice stop">
        Could not load this resident&apos;s other cases: {loadErr}{' '}
        <button type="button" className="pa-open" onClick={() => { void load(); }}>Try again</button>
      </div>
    );
  }
  if (!data) return null;
  if (data.refusal) {
    const dup = data.refusal.detail?.duplicateOf;
    return (
      <p className="pa-text none" role="status">
        {data.refusal.message || REFUSAL_WORDS[data.refusal.code] || 'This case cannot be marked as a duplicate.'}
        {dup ? <> See {link(dup, shortCase(dup))}.</> : null}
      </p>
    );
  }
  if (!data.candidates.length) {
    return (
      <p className="pa-text none" role="status">
        This resident has no other case, so this one cannot be a duplicate. If this lead should not be worked, change its status instead.
      </p>
    );
  }

  const choosable = data.candidates.filter((c) => c.selectable);
  const chosen = data.candidates.find((c) => c.id === picked && c.selectable) || null;
  const noteOk = note.trim().length >= NOTE_MIN;
  const groupId = `dup-pick-${caseId}`;

  const submit = async () => {
    if (!chosen || !noteOk || busy) return;
    setBusy(true); setErr(null);
    try {
      const r = await advocateApi.markDuplicate(caseId, { keep_case_id: chosen.id, note: note.trim(), idempotency_key: key.current });
      if (!r.success || !r.data) {
        const words = refusalText(r);
        const dup = r.detail?.duplicateOf;
        if (dup && (r.code === 'KEEP_IS_DUPLICATE' || r.code === 'ALREADY_DUPLICATE')) refuse(words, <>{words} See {link(dup, shortCase(dup))}.</>);
        else refuse(words);
        // A refusal (4xx) is final for this choice: the next attempt is a new act. A dropped connection or a server
        // failure may or may not have recorded it, so the retry keeps the key and the server answers it as a replay.
        if (r.status !== undefined && r.status < 500) key.current = freshKey();
        return;
      }
      const d = r.data;
      await onDone(d.replayed
        ? `Already recorded: this case is closed as a duplicate of ${shortCase(d.keepCaseId)}.`
        : `Closed as a duplicate of ${shortCase(d.keepCaseId)}. It has left your working views and stays in History; the work continues on that case.`);
    } catch {
      refuse('Could not reach the server. Nothing was changed.');
    } finally { setBusy(false); }
  };

  return (
    <form className="pa-form" aria-label="Mark as duplicate" onSubmit={(e) => {
      e.preventDefault();
      if (confirming) { void submit(); return; }
      if (!chosen || !noteOk) { setTried(true); return; }
      setErr(null); setConfirming(true);
    }}>
      <p className="pa-text none">Closes this case as a duplicate of another case for the same resident. The case you choose is not changed, and this one cannot be reopened afterwards: the work continues on the case you keep.</p>
      <fieldset className="pa-products" aria-describedby={tried && !chosen ? `${groupId}-missing` : undefined}>
        <legend>Which case does this one duplicate?</legend>
        <ul className="pa-hits" role="list">
          {data.candidates.map((c) => {
            const id = `${groupId}-${c.id}`;
            return (
              <li key={c.id}>
                <label htmlFor={id} className={`pa-product pa-dup-pick${picked === c.id ? ' on' : ''}${c.selectable ? '' : ' off'}`}>
                  <input id={id} type="radio" name={groupId} value={c.id} checked={picked === c.id} disabled={!c.selectable || busy}
                    onChange={() => { setPicked(c.id); fresh(); }} aria-describedby={c.refusal ? `${id}-why` : undefined} />
                  <span className="pa-product-body">
                    <span>{describeCandidate(c)}</span>
                    {c.refusal && <span id={`${id}-why`} className="pa-sub-line">{c.refusal.message || REFUSAL_WORDS[c.refusal.code]}</span>}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
        {!choosable.length && <p className="pa-text none" role="status">None of this resident&apos;s other cases can be chosen; each says why.</p>}
      </fieldset>
      {tried && !chosen && <p id={`${groupId}-missing`} className="pa-notice stop" role="alert">Choose the case this one duplicates.</p>}
      <label>
        <span>Why (required, kept on the record)</span>
        <textarea value={note} onChange={(e) => { setNote(e.target.value); fresh(); }} maxLength={NOTE_MAX} rows={2} aria-label="Duplicate note" aria-required
          aria-invalid={tried && !noteOk ? true : undefined} aria-describedby={tried && !noteOk ? `${groupId}-note-missing` : undefined} disabled={busy} />
      </label>
      {tried && !noteOk && <p id={`${groupId}-note-missing`} className="pa-notice stop" role="alert">Say why this case is a duplicate (for example, the same TWL entered twice).</p>}
      {confirming && chosen ? (
        <div className="pa-confirm" role="group" aria-label="Confirm mark as duplicate"
          onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); stepBack(); } }}>
          <p className="pa-text"><strong>Close this case as a duplicate of {shortCase(chosen.id)}?</strong> It leaves your working views today and stays findable in History and in resident search.</p>
          <div className="btns">
            <button ref={confirmRef} type="submit" className="pa-open pa-primary" disabled={busy}>{busy ? 'Closing…' : 'Close as duplicate'}</button>
            <button type="button" className="pa-open" onClick={stepBack} disabled={busy}>Back</button>
          </div>
        </div>
      ) : (
        <div className="btns"><button ref={triggerRef} type="submit" className="pa-open pa-primary" disabled={!choosable.length}>Mark as duplicate…</button></div>
      )}
      {err && <p className="pa-notice stop" role="alert">{err}</p>}
    </form>
  );
}
